import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { CloseIcon } from "@/components/icons/CloseIcon";

import { cn } from "@/lib/utils";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

export const DIALOG_OVERLAY_CL =
  "fixed inset-0 z-[200] bg-black/20 backdrop-blur-sm [mask-image:linear-gradient(to_bottom,black_100%,transparent)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 ![animation-duration:600ms] ![animation-timing-function:cubic-bezier(0.16,1,0.3,1)] ![animation-fill-mode:forwards]";

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(DIALOG_OVERLAY_CL, className)}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    hideCloseButton?: boolean;
    position?:
      | "center"
      | "right"
      | "left-always"
      | "right-always"
      | "full-screen"
      | "full-screen-zoom"
      | "center-always";
    overlayClassName?: string;
    /** Portal target. Omit for the default (document.body, viewport-wide).
     *  Pass a specific element to scope the dialog — overlay included — to
     *  that element's own bounds instead of the whole viewport; the caller
     *  is then responsible for `position: relative` on that element and for
     *  overriding the `fixed` positioning classes (base/position props here
     *  still assume a viewport-fixed dialog) to `absolute`. */
    container?: HTMLElement | null;
  }
>(
  (
    {
      className,
      children,
      hideCloseButton,
      position = "center",
      overlayClassName,
      container,
      ...props
    },
    ref
  ) => (
    <DialogPortal container={container}>
      <DialogOverlay
        data-testid="dialog-overlay"
        className={overlayClassName}
      />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "fixed z-[200] grid ![animation-duration:600ms] ![animation-timing-function:cubic-bezier(0.16,1,0.3,1)]",
          "w-full sm:max-w-md px-4 py-5 xs:p-6",
          "bg-card-bg border border-border-subtle shadow-2xl",
          "bottom-0 left-0 right-0 rounded-t-20 border-b-0",
          "max-h-[calc(100dvh-2rem)] overflow-y-auto [overscroll-behavior:contain]",
          "data-[state=open]:animate-in data-[state=open]:fade-in-0",
          "data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
          position === "center" && [
            "sm:rounded-20",
            "sm:bottom-auto sm:left-[50%] sm:right-auto sm:top-[50%] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:border-b",
            "sm:max-h-[calc(100dvh-4rem)]",
            "max-sm:data-[state=open]:slide-in-from-bottom-full sm:data-[state=open]:zoom-in-95",
            "max-sm:data-[state=closed]:slide-out-to-bottom-full sm:data-[state=closed]:zoom-out-95",
          ],
          position === "center-always" && [
            "bottom-auto right-auto",
            "left-1/2 translate-x-[-50%]",
            "top-[50%] translate-y-[-50%]",
            "rounded-b-20 border-b",
            "data-[state=open]:zoom-in-95",
            "data-[state=closed]:zoom-out-95",
          ],
          position === "right" && [
            "sm:bottom-0 sm:top-0 sm:right-0 sm:left-auto sm:translate-x-0 sm:translate-y-0",
            "h-dvh sm:max-h-dvh sm:rounded-none sm:border-l sm:border-t-0 sm:border-b-0 sm:border-r-0",
            "max-sm:data-[state=open]:slide-in-from-bottom-full sm:data-[state=open]:slide-in-from-right-full",
            "max-sm:data-[state=closed]:slide-out-to-bottom-full sm:data-[state=closed]:slide-out-to-right-full",
          ],
          position === "left-always" && [
            "top-0 right-auto translate-x-0",
            "h-dvh max-h-dvh rounded-t-none border-r border-t-0 border-b-0 border-l-0",
            "data-[state=open]:slide-in-from-left-full",
            "data-[state=closed]:slide-out-to-left-full",
          ],
          position === "right-always" && [
            "top-0 left-auto translate-x-0",
            "h-dvh max-h-dvh rounded-t-none border-l border-t-0 border-b-0 border-r-0",
            "data-[state=open]:slide-in-from-right-full",
            "data-[state=closed]:slide-out-to-right-full",
          ],
          position === "full-screen" && [
            "!inset-0 max-w-none sm:max-w-none h-dvh max-h-none sm:max-h-none p-0 xs:p-0 border-none sm:border-none rounded-none sm:rounded-none bg-bg-base sm:translate-x-0 sm:translate-y-0",
          ],
          position === "full-screen-zoom" && [
            "!inset-0 max-w-none sm:max-w-none h-dvh max-h-none sm:max-h-none p-0 xs:p-0 border-none sm:border-none rounded-none sm:rounded-none bg-bg-base sm:translate-x-0 sm:translate-y-0",
            "data-[state=open]:zoom-in-95",
            "data-[state=closed]:zoom-out-95",
          ],
          className
        )}
        {...props}
      >
        {children}
        {!hideCloseButton && (
          <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-border-subtle focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-bg-muted data-[state=open]:text-fg-muted">
            <CloseIcon className="h-5 w-5" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("flex flex-col gap-y-1.5 text-left mb-5", className)}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end gap-x-3 gap-y-2.5 mt-6",
      className
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-5 font-semibold text-fg-default tracking-[-0.01em] leading-none",
      className
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-3.5 text-fg-subtle leading-relaxed", className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

const DialogHeaderRich = ({
  title,
  titleId,
  description,
  icon,
  iconBgc = "bg-bg-muted",
  iconBgcStyle,
}: {
  title: string;
  titleId?: string;
  description: string;
  icon: React.ReactNode;
  iconBgc?: string;
  iconBgcStyle?: string;
}) => {
  return (
    <div className="flex flex-col items-center text-center">
      <div
        className={`flex h-14 w-14 items-center justify-center overflow-hidden ${iconBgc} rounded-20 text-6`}
        style={iconBgcStyle ? { background: iconBgcStyle } : undefined}
        aria-hidden="true"
      >
        {icon}
      </div>
      <h2
        id={titleId}
        className="mt-4 text-4.5 font-semibold text-fg-default tracking-normal leading-normal"
      >
        {title}
      </h2>

      <p className="mt-1.5 text-3.5 text-fg-subtle">{description}</p>
    </div>
  );
};

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogHeaderRich,
};
